<template>
  <div class="main-container">
    <h1>{{ $t("arkemaUsers.title") }}</h1>
    <div
      class="group-button-table d-flex flex-wrap align-items-center coefficient-toolbar mt-2 justify-content-between"
    >
      <div class="d-flex">
        <button
          type="button"
          class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
          @click="add"
        >
          <b-icon
            icon="plus"
            class="mr-1"
            scale="1.5"
            aria-hidden="true"
            variant="danger"
          />
          {{ $t("arkemaUsers.add") }}
        </button>
        <button
          type="button"
          class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
          @click="openImport"
        >
          <b-icon
            icon="upload"
            class="mr-2"
            font-scale="0.8"
            scale="1.5"
            aria-hidden="true"
            variant="danger"
          />
          {{ $t("arkemaUsers.import") }}
        </button>
      </div>
      <div class="d-flex">
        <button
          type="button"
          class="btn btn-primary mr-2 mb-1 d-flex align-items-center"
          :disabled="!hasModifications"
          @click="save"
        >
          {{ $t("arkemaUsers.save") }}
        </button>
      </div>
    </div>
    <DataTable
      ref="table"
      :columns="dtColumns"
      :url="url"
      trackBy="id"
      :searchable="true"
      :searchLabel="$t('arkemaUsers.search').toString()"
      :parseResponse="parseResponse"
      :showActions="false"
      :rowClass="rowClassFn"
    >
      <template #editable-idSap="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.idSap"
          @input="markModified(row)"
        />
      </template>
      <template #editable-firstName="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.firstName"
          @input="markModified(row)"
        />
      </template>
      <template #editable-lastName="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.lastName"
          @input="markModified(row)"
        />
      </template>
      <template #sitesCell="{ row }">
        <EntrepriseSitesCell
          :row="{ entreprise: { sites: row.sites }, translation: { name: '' } }"
          @edit-sites="openSitesRow(row)"
        />
      </template>
      <template #editable-mail="{ row }">
        <div class="wrap-text">
          <input
            class="form-control form-control-sm"
            type="email"
            v-model="row.mail"
            @input="markModified(row)"
          />
        </div>
      </template>
      <template #dropdown-role="{ row }">
        <div class="custom-actions">
          <div
            v-if="!editingRole[row.id]"
            :class="{ 'is-disabled': false }"
            @click="beginEditRole(row)"
          >
            {{ displayRoleText(row) }}
          </div>
          <b-form-select
            v-else
            :ref="'roleSel-' + row.id"
            size="sm"
            v-model="row.idRole"
            @change="handleRoleSelectChange(row)"
            @blur="endEditRole(row)"
          >
            <option
              v-for="r in roleOptions"
              :key="r.role.id"
              :value="r.role.id"
            >
              {{ r.translation.name }}
            </option>
          </b-form-select>
        </div>
      </template>
      <template #switch-isEnabled="{ row }">
        <button
          type="button"
          class="btn btn-switch toggle-switch"
          :class="row.isEnabled ? 'active-last' : 'active-first'"
          @click="toggleEnabled(row)"
        >
          <span class="switch-toggler" />
        </button>
      </template>
    </DataTable>
    <div
      class="group-button-table d-flex flex-wrap align-items-center coefficient-toolbar mt-2 justify-content-between"
    >
      <div class="d-flex">
        <button
          type="button"
          class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
          @click="add"
        >
          <b-icon
            icon="plus"
            class="mr-1"
            scale="1.5"
            aria-hidden="true"
            variant="danger"
          />
          {{ $t("arkemaUsers.add") }}
        </button>
      </div>
      <div class="d-flex">
        <button
          type="button"
          class="btn btn-primary mr-2 mb-1 d-flex align-items-center"
          :disabled="!hasModifications"
          @click="save"
        >
          {{ $t("arkemaUsers.save") }}
        </button>
      </div>
    </div>
    <SitesTableModal
      :show="showSitesModal"
      @close="showSitesModal = false"
      @validate="validate"
      :props="propsSitesModal"
    />
    <ImportUsersModal
      ref="importModal"
      @handleImportSuccess="refresh"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import UserRoleService from "@/services/UserRoleService";
import UserService from "@/services/UserService";
import ManageUser from "@/models/ManageUser";
import { UserProfilEnum } from "@/utils/Enums";
import Constants from "@/utils/Constants";
import DataTable from "@/components/Shared/Table/DataTable.vue";
import ImportUsersModal from "@/components/Users/ImportUsersModal.vue";

type Data = {
  columns: Array<Object>;
  rows: Array<Object>;
};

@Component({
  components: {
    DataTable,
    EntrepriseSitesCell: () =>
      import("@/components/Entreprises/EntrepriseSitesCell.vue"),
    SitesTableModal: () =>
      import("@/components/Shared/Table/SitesTableModal.vue"),
    ImportUsersModal: () => import("@/components/Users/ImportUsersModal.vue"),
  },
})
export default class ArkemaUsers extends Vue {
  public data: Data = {
    columns: [],
    rows: [],
  };
  private modifiedUsers: Array<ManageUser> = [];
  private url: string = UserService.getTableUrl(
    "arkemaUser",
    this.$i18n.locale,
  );

  // Props for Modal on categoy field
  // Sites Modal
  private showSitesModal: Boolean = false;
  private propsSitesModal: any = false;
  private roleOptions: any[] = [];
  private selectedRow: string[] = [];
  private editingRole: Record<string, boolean> = {};

  // Validate button on modals
  public validate(user: any) {
    this.markModified(user);
    this.showSitesModal = false;
  }

  public async save() {
    for (let i = 0; i < this.modifiedUsers.length; i++) {
      const modifiedUser = this.modifiedUsers[i];

      if (modifiedUser.firstName == "") {
        this.sendErrorNotification(
          this.$t("arkemaUsers.updateFailed").toString(),
          this.$t("arkemaUsers.propertyFirstNameEmpty").toString(),
        );
        return;
      } else if (modifiedUser.lastName == "") {
        this.sendErrorNotification(
          this.$t("arkemaUsers.updateFailed").toString(),
          this.$t("arkemaUsers.propertyLastNameEmpty").toString(),
        );
        return;
      } else if (modifiedUser.idSap == "") {
        this.sendErrorNotification(
          this.$t("arkemaUsers.updateFailed").toString(),
          this.$t("arkemaUsers.propertyIdSapEmpty").toString(),
        );
        return;
      } else if (modifiedUser.mail == "") {
        this.sendErrorNotification(
          this.$t("arkemaUsers.updateFailed").toString(),
          this.$t("arkemaUsers.propertyMailEmpty").toString(),
        );
        return;
      }
    }
    try {
      await UserService.saveUsers(this.modifiedUsers, this.$i18n.locale);
      this.$notify({
        group: "global",
        type: "success",
        title: this.$t("externalUsers.updateSuccess.title").toString(),
        text: this.$t("externalUsers.updateSuccess.description").toString(),
      });
      this.refresh();
    } catch (err: any) {
      let errMessage = this.$t("arkemaUsers.uknownError").toString();
      if (err && err.response && err.response.status === 400) {
        switch (err.response.data && err.response.data.errorCode) {
          case "duplicateUserLogin":
            errMessage = this.$t("arkemaUsers.duplicateUserLogin").toString();
            break;
          case "duplicateUserMail":
            errMessage = this.$t("arkemaUsers.duplicateUserMail").toString();
            break;
          case "duplicateUserIdSap":
            errMessage = this.$t("arkemaUsers.duplicateUserIdSap").toString();
            break;
          case "idSapNotValid":
            errMessage = this.$t("arkemaUsers.idSapNotValid").toString();
            break;
          case "lastCentralAdministratorEnabled":
            errMessage = this.$t(
              "arkemaUsers.lastCentralAdministratorEnabled",
            ).toString();
            break;
          case "userMustHaveRole":
            errMessage = this.$t("arkemaUsers.userMustHaveRole").toString();
            break;
          case "notEnoughPermissions":
            errMessage = this.$t("arkemaUsers.notEnoughPermissions").toString();
            break;
          case "idRoleRequired":
            errMessage = this.$t("arkemaUsers.roleIsRequired").toString();
            break;
        }
      }
      this.sendErrorNotification(
        this.$t("arkemaUsers.updateFailed").toString(),
        errMessage,
      );
    }
  }
  private refresh() {
    this.modifiedUsers = [];
    const table: any = this.$refs.table;
    if (table && table.api && this.url) table.api.load(this.url);
    this.selectedRow = [];
  }
  private sendErrorNotification(title: string, text: string) {
    this.$notify({
      group: "globalError",
      type: "error",
      title: title,
      text: text,
      duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
    });
  }

  // Open modal based on property
  public openSitesRow(row: any) {
    this.showSitesModal = true;
    this.propsSitesModal = { rowData: row, rowField: { property: "sites" } };
  }

  public add(): void {
    const newUser = new ManageUser();
    newUser.keyProfil = "arkemaUser";
    const table: any = this.$refs.table;
    if (table && table.api) table.api.rows.unshift(newUser);
    this.markModified(newUser);
    const id = (newUser as any).id;
    if (id && !this.selectedRow.includes(id)) this.selectedRow.push(id);
  }

  public openImport(): void {
    (this.$refs.importModal as any).openImportModal();
  }

  public async loadElements() {
    const roles = await UserRoleService.getRolesByProfil(
      this.$i18n.locale,
      UserProfilEnum.arkemaUser,
    );
    this.roleOptions = roles.data;
    this.data.columns = [
      {
        name: "editable",
        sortField: "idSap",
        title: this.$t("arkemaUsers.sapId").toString(),
        property: "idSap",
        length: 8,
      },
      {
        name: "editable",
        sortField: "firstName",
        title: this.$t("arkemaUsers.firstName").toString(),
        property: "firstName",
      },
      {
        name: "editable",
        sortField: "lastName",
        title: this.$t("arkemaUsers.lastName").toString(),
        property: "lastName",
      },
      {
        name: "category",
        sortField: "sites",
        title: this.$t("arkemaUsers.sites").toString(),
        property: "sites",
        display: "translation.name",
      },
      {
        name: "editable",
        sortField: "mail",
        title: this.$t("arkemaUsers.email"),
        property: "mail",
        type: "email",
      },
      {
        name: "dropdown",
        sortField: "nameRole",
        title: this.$t("arkemaUsers.roles").toString(),
        property: "idRole",
        options: roles.data,
        valueField: "role.id",
        textField: "translation.name",
        display: "nameRole",
        default: 0,
      },
      {
        name: "switch",
        sortField: "isEnabled",
        title: this.$t("arkemaUsers.enabled").toString(),
        property: "isEnabled",
      },
    ];
  }
  public mounted() {
    this.loadElements();
  }

  @Watch("$i18n.locale")
  private onLocaleChange() {
    this.loadElements();
    this.url = UserService.getTableUrl("arkemaUser", this.$i18n.locale);
  }

  private get dtColumns(): Array<{
    key: string;
    label: string;
    sortField?: string;
    slot?: string;
  }> {
    return [
      {
        key: "idSap",
        label: this.$t("arkemaUsers.sapId").toString(),
        sortField: "idSap",
        slot: "editable-idSap",
      },
      {
        key: "firstName",
        label: this.$t("arkemaUsers.firstName").toString(),
        sortField: "firstName",
        slot: "editable-firstName",
      },
      {
        key: "lastName",
        label: this.$t("arkemaUsers.lastName").toString(),
        sortField: "lastName",
        slot: "editable-lastName",
      },
      {
        key: "sites",
        label: this.$t("arkemaUsers.sites").toString(),
        sortField: "sites",
        slot: "sitesCell",
      },
      {
        key: "mail",
        label: this.$t("arkemaUsers.email").toString(),
        sortField: "mail",
        slot: "editable-mail",
      },
      {
        key: "nameRole",
        label: this.$t("arkemaUsers.roles").toString(),
        sortField: "nameRole",
        slot: "dropdown-role",
      },
      {
        key: "isEnabled",
        label: this.$t("arkemaUsers.enabled").toString(),
        sortField: "isEnabled",
        slot: "switch-isEnabled",
      },
    ];
  }

  private parseResponse(resp: any) {
    const d = resp?.data || [];
    const rows = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
    return { rows, pagination: resp?.pagination };
  }

  private markModified(row: any) {
    const idx = this.modifiedUsers.findIndex((u: any) => u.id === row.id);
    if (idx === -1) this.modifiedUsers.push(row);
    else this.modifiedUsers.splice(idx, 1, row);
  }

  get hasModifications(): boolean {
    return this.modifiedUsers.length > 0;
  }

  private onRoleChange(row: any) {
    const selected = this.roleOptions.find(
      (r: any) => r.role?.id === row.idRole,
    );
    row.nameRole = selected ? selected.translation?.name : "";
    this.markModified(row);
  }

  private displayRoleText(row: any): string {
    const name = row.nameRole;
    if (name && name.length) return name;
    const selected = this.roleOptions.find(
      (r: any) => r.role?.id === row.idRole,
    );
    return selected ? selected.translation?.name || "" : "--";
  }
  private beginEditRole(row: any) {
    this.$set(this.editingRole, row.id, true);
    this.$nextTick(() => {
      const refName = `roleSel-${row.id}`;
      const el: any = (this as any).$refs[refName];
      if (el && el.focus) el.focus();
    });
  }
  private endEditRole(row: any) {
    this.$set(this.editingRole, row.id, false);
  }
  private handleRoleSelectChange(row: any) {
    this.onRoleChange(row);
    this.endEditRole(row);
  }

  private toggleEnabled(row: any) {
    row.isEnabled = !row.isEnabled;
    this.markModified(row);
  }

  private rowClassFn(row: any) {
    const id = row.id;
    return this.selectedRow.includes(id) ? "selected-row" : "";
  }
}
</script>
<style scoped>
.wrap-text {
  white-space: normal;
  word-break: break-word;
}
.dropdown-display {
  min-height: 28px;
  line-height: 28px;
  padding: 0 6px;
  border: 1px solid #ced4da;
  border-radius: 0.2rem;
  background-color: #fff;
  font-size: 0.875rem;
  cursor: pointer;
}
.dropdown-display.is-disabled {
  cursor: default;
  background-color: #e9ecef;
}
</style>
